import { useCallback } from 'react';

import { useCheckoutContext } from '@zooplus/checkout-state';
import { getLanguageFromLocale } from '@zooplus/commons-lang';
import { trackEvent as trackEventUsingLibrary, TrackingData } from '@zooplus/tracker-listener';

import { getAppConfig } from '@config/get-app-config';
import { logError } from '@services/sentry-service';
import {
  CHECKOUT_STATE_PROJECT_ID,
  AREA_TITLE,
  ADOBE_TRACKING,
  PAGE_LOAD_COMPLETE_TIMEOUT,
  CART_VERSION,
  TRACKING_DATA_EVENT,
  CUSTOMER_TYPE,
  TRACKING_PAGE,
  TARGET_ACTION_ID,
  TARGET_COMPONENT_NAME,
} from '@utilities/constants';
import { getCommonTrackingData } from '@utilities/data-tracking';

import useAdobeTracking from './use-adobe-tracking';

const usePageTracking = () => {
  const { sendAdobeEvent, trackEvent } = useAdobeTracking();
  const { checkoutState, cartVersion } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const {
    PAGE_LOAD_INIT,
    PAGE_LOAD_COMPLETE,
    PAGE_VIEW_EVENT_TITLE,
    CART_PAGE_IDENTIFIER,
    CLICK_EVENT_GENERIC,
    EMPTY_CART_PAGE_IDENTIFIER,
    PAGE_REFRESH,
  } = ADOBE_TRACKING;

  const cart = checkoutState?.cart;
  const isCartApiV2: boolean = cartVersion === CART_VERSION.V2;
  const { site, deviceType } = getAppConfig();

  let selectedDeliveryCountryIsoCode = cart?.selectedDeliveryCountryIsoCode;
  if (isCartApiV2) {
    selectedDeliveryCountryIsoCode = cart?.deliveryDetails?.selectedDeliveryCountryIsoCode || '';
  }
  const togglesAndExperiments = checkoutState?.togglesAndExperiments;
  const customerId = checkoutState?.customer?.customerId;
  const customerType = checkoutState?.customer?.customerType;
  const minimumOrderValue = checkoutState?.stateAttributes?.minimumOrderValue;

  const trackPageLoadEvent = useCallback((eventType: string) => {
    if (eventType === PAGE_LOAD_INIT) {
      sendAdobeEvent({ event: PAGE_LOAD_INIT });
    } else if (eventType === PAGE_LOAD_COMPLETE) {
      setTimeout(() => {
        sendAdobeEvent({ event: PAGE_LOAD_COMPLETE });
      }, PAGE_LOAD_COMPLETE_TIMEOUT);
    }
  }, []);

  const trackPageViewUsingAdobe = useCallback(
    (url: string) => {
      sendAdobeEvent({
        event: PAGE_VIEW_EVENT_TITLE,
        page: {
          pageType: AREA_TITLE,
          pageTypelv2: CART_PAGE_IDENTIFIER,
          shopBrand: site.realm,
          shopCountry: selectedDeliveryCountryIsoCode,
          shopLanguage: getLanguageFromLocale(site.locale) || '',
          siteId: site.id,
          url,
        },
        togglesAndExperiments,
        customerId,
        customerType,
        minimumOrderValue,
      });
    },
    [
      selectedDeliveryCountryIsoCode,
      togglesAndExperiments,
      customerId,
      customerType,
      minimumOrderValue,
    ],
  );

  const trackPageViewUsingTrackerListener = useCallback(
    (page: string) => {
      const { customerId, customerType } = checkoutState?.customer || {};
      trackEventUsingLibrary(<TrackingData>{
        event: TRACKING_DATA_EVENT.PAGE_VIEW,
        page: page,
        detail: {
          sid: cart?.sid,
          ...getCommonTrackingData(),
          customer: {
            id: customerId,
            guest: customerType === CUSTOMER_TYPE.GUEST_CUSTOMER,
          },
        },
      });
    },
    [cart, checkoutState?.customer],
  );

  const trackEventUsingTrackerListener = useCallback(
    (
      eventName: TRACKING_DATA_EVENT,
      targetActionId?: TARGET_ACTION_ID,
      targetComponentName?: TARGET_COMPONENT_NAME,
    ) => {
      try {
        const pathName = window.location.pathname as keyof typeof TRACKING_PAGE;
        const customerData = {
          id: customerId,
          guest: customerType === CUSTOMER_TYPE.GUEST_CUSTOMER,
        };

        const trackingPayload = {
          event: eventName,
          page: TRACKING_PAGE[pathName] ?? 'unknown',
          detail: {
            sid: cart?.sid,
            platform: deviceType,
            ...getCommonTrackingData(),
            ...(customerId ? { customer: customerData } : {}),
            ...(targetActionId
              ? { target: { id: targetActionId, name: targetComponentName } }
              : {}),
          },
        } as TrackingData;

        trackEventUsingLibrary(trackingPayload);
      } catch (error) {
        logError(new Error(`Error tracking event "${eventName}": ${error}`));
      }
    },
    [cart?.sid, customerId, customerType, deviceType],
  );

  const trackPageRefreshButtonEvent = () => {
    trackEvent(CLICK_EVENT_GENERIC, PAGE_REFRESH, {
      area: AREA_TITLE,
      identifier: EMPTY_CART_PAGE_IDENTIFIER,
      targetLink: window.location.href,
    });
  };

  return {
    trackPageLoadEvent,
    trackPageViewUsingAdobe,
    trackPageRefreshButtonEvent,
    trackPageViewUsingTrackerListener,
    trackEventUsingTrackerListener,
  };
};

export default usePageTracking;
